// Dictionary — a calm, learner-friendly lookup over the license-isolated
// `dictionary_entries` collection (built host-side by scripts/dictionary_build.js
// from Wiktextract/Kaikki + Tatoeba; see pb_setup/README.md §9).
//
// Deliberately NOT a raw lexicographic dump. Each entry reads as a tidy card:
// headword + gender colour + audio, a one-line meaning preview, then a detail
// panel with the top senses (rest behind a "more senses" toggle), IPA, an
// example sentence, and cross-link chips back to the verb/vocabulary the word is
// already taught in. The attribution footer is a LICENCE CONDITION and always
// renders on any surface that shows dictionary data.
//
// Read-only by design: the dictionary is a separate, license-isolated collection
// and is not part of user_workspace (no pins/notes here).
import { fetchDictionary } from "../lib/data.js";
import { isAuthed } from "../lib/auth.js";
import { defineWord, aiErrorMessage } from "../lib/ai.js";
import { ALL_VERBS } from "../data/index.js";
import { VOCABULARY_CARDS } from "../data/vocabulary.js";
import { navigate } from "../router/router.js";
import { escapeHtml, highlightMatch } from "../ui/helpers.js";
import { speakButtonHTML } from "../lib/audio.js";
import { dictionaryAttributionHTML } from "../lib/dictionaryAttribution.js";

const ARTICLE_RE = /^(?:(?:il|lo|la|i|gli|le|un|uno|una)\s+|l'|un')/i;

let cache = null; // { entries: [] }
let xref = null; // { verbs:Set, vocab:Map }

async function loadData() {
  const entries = await fetchDictionary();
  cache = { entries };
  return cache;
}

// Build the taught-word cross-reference once: which headwords are also a verb we
// conjugate, or a vocabulary card the learner meets.
function buildXref() {
  if (xref) return xref;
  const verbs = new Set();
  for (const v of ALL_VERBS) {
    const k = (v.inf || "").toLowerCase().trim();
    if (k) verbs.add(k);
  }
  const vocab = new Map();
  for (const c of VOCABULARY_CARDS) {
    const k = (c.spanish || "").toLowerCase().trim().replace(ARTICLE_RE, "");
    if (k && !vocab.has(k)) vocab.set(k, c);
  }
  xref = { verbs, vocab };
  return xref;
}

function posBadge(pos) {
  const map = {
    noun: "bg-sage-50 text-sage-600 border-sage-100",
    verb: "bg-amber-50 text-amber-700 border-amber-100",
    adjective: "bg-sky-50 text-sky-700 border-sky-100",
    adverb: "bg-violet-50 text-violet-700 border-violet-100",
    preposition: "bg-teal-50 text-teal-700 border-teal-100",
    conjunction: "bg-teal-50 text-teal-700 border-teal-100",
    pronoun: "bg-indigo-50 text-indigo-700 border-indigo-100",
    determiner: "bg-indigo-50 text-indigo-700 border-indigo-100",
    number: "bg-stone-50 text-stone-600 border-stone-100",
    interjection: "bg-rose-50 text-rose-700 border-rose-100",
    phrase: "bg-rose-50 text-rose-700 border-rose-100",
  };
  return map[pos] || "bg-sage-50 text-sage-600 border-sage-100";
}

function genderTag(gender) {
  if (gender === "m") return `<span class="text-[10px] text-blue-500 font-mono">el</span>`;
  if (gender === "f") return `<span class="text-[10px] text-rose-500 font-mono">la</span>`;
  if (gender === "mf") return `<span class="text-[10px] text-sage-500 font-mono">el/la</span>`;
  return "";
}

// glosses_json may arrive as an array (PB json field) or, defensively, a JSON
// string — normalise to a clean string[].
function glossList(entry) {
  let g = entry.glosses_json;
  if (typeof g === "string") {
    try {
      g = JSON.parse(g);
    } catch {
      g = g ? [g] : [];
    }
  }
  return Array.isArray(g) ? g.filter(Boolean) : [];
}

export async function dictionaryView(app, params = {}) {
  app.innerHTML = `<div class="py-16 text-center text-sage-400 text-sm">Loading dictionary…</div>`;
  const data = await loadData();
  const entries = data.entries;

  // Pre-seed / offline: the host hasn't loaded the dataset yet. Explain calmly
  // rather than showing an error — no dictionary data means no licence surface.
  if (!entries.length) {
    app.innerHTML = `
      <section class="max-w-2xl">
        <div class="mb-6">
          <h2 class="text-2xl font-bold text-sage-800">📕 Dictionary</h2>
          <p class="text-sage-600 text-sm mt-1">A per-word lookup for every verb and A1–B1 vocabulary word, drawn from openly-licensed data.</p>
        </div>
        <div class="rounded-2xl border border-dashed border-sage-200 bg-sand-50/50 p-6 text-center">
          <p class="text-3xl mb-2">📭</p>
          <p class="text-sage-700 font-medium">The dictionary hasn’t been loaded yet.</p>
          <p class="text-xs text-sage-500 mt-2 leading-relaxed">It’s built from open datasets on the host (Wiktextract / Kaikki + Tatoeba) and loaded into the backend separately from the rest of Azzurro. Once that one-time build runs, definitions appear here.</p>
        </div>
      </section>`;
    return;
  }

  const tiers = [...new Set(entries.map((e) => e.tier).filter(Boolean))].sort();

  app.innerHTML = `
    <section>
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-sage-800">📕 Dictionary</h2>
        <p class="text-sage-600 text-sm mt-1">${entries.length.toLocaleString()} words — every verb and A1–B1 vocabulary word, with meanings, pronunciation, and an example in context.</p>
      </div>
      <div class="relative mb-3">
        <div class="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 pointer-events-none">🔍</div>
        <input id="dict-search" type="text" placeholder="Look up a word — 'parlare', 'casa', 'happy'…"
          class="w-full pl-11 pr-4 py-3 rounded-xl border border-sage-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-base" />
      </div>
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <span class="text-xs text-sage-500 uppercase tracking-wide mr-1">Level:</span>
        <button class="tier-chip" data-tier="">All</button>
        ${tiers.map((t) => `<button class="tier-chip" data-tier="${t}">${t}</button>`).join("")}
      </div>
      <p id="dict-count" class="text-xs text-sage-500 mb-3"></p>
      <div id="dict-list"></div>
      <div id="dict-attrib"></div>
    </section>`;

  const input = app.querySelector("#dict-search");
  const list = app.querySelector("#dict-list");
  const count = app.querySelector("#dict-count");
  const attrib = app.querySelector("#dict-attrib");
  let tier = tiers.includes(params.tier) ? params.tier : "";

  // Attribution is a licence condition — render it once at the foot of the page
  // wherever dictionary data is shown.
  attrib.innerHTML = dictionaryAttributionHTML();

  function paintChips() {
    app.querySelectorAll(".tier-chip").forEach((b) => {
      const on = b.dataset.tier === tier;
      b.className = `tier-chip px-3 py-1 rounded-full text-xs font-medium border transition ${
        on ? "bg-sage-500 text-white border-sage-500" : "bg-white text-sage-600 border-sage-200 hover:border-sage-300"
      }`;
    });
  }

  function render() {
    const q = input.value.trim().toLowerCase();
    const results = entries.filter((e) => {
      if (tier && e.tier !== tier) return false;
      if (!q) return true;
      if (e.headword.toLowerCase().includes(q)) return true;
      if ((e.ipa || "").toLowerCase().includes(q)) return true;
      return glossList(e).some((g) => g.toLowerCase().includes(q));
    });

    count.innerHTML = q
      ? `<b>${results.length}</b> match${results.length === 1 ? "" : "es"} for "<span class='text-sage-700'>${escapeHtml(q)}</span>"`
      : `${results.length} word${results.length === 1 ? "" : "s"}${tier ? " · " + tier : ""}`;

    if (!results.length) {
      const canAi = q && isAuthed();
      list.innerHTML = `<div class="text-center py-10 text-sage-500">
        <p class="text-2xl mb-2">🔍</p><p>No entries found${q ? ` for "${escapeHtml(q)}"` : ""}.</p>
        ${
          canAi
            ? `<button id="dict-ai" class="mt-3 bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Look up “${escapeHtml(q)}” with AI</button>
               <p id="dict-ai-msg" class="text-xs mt-2 text-sage-400">Adds it to the dictionary for everyone.</p>`
            : `<p class="text-xs mt-2 text-sage-400">${q ? "Sign in to look up words beyond the core set with AI." : "Type a word to search."}</p>`
        }
      </div>`;
      const aiBtn = list.querySelector("#dict-ai");
      if (aiBtn) {
        aiBtn.addEventListener("click", async () => {
          const msg = list.querySelector("#dict-ai-msg");
          aiBtn.disabled = true;
          aiBtn.textContent = "Looking up…";
          try {
            const res = await defineWord(q);
            const entry = res && res.entry;
            if (!entry || !entry.headword) throw new Error("not found");
            // Add to the in-memory set (de-duped) and open it.
            if (!entries.some((x) => x.id && x.id === entry.id)) entries.push(entry);
            render();
            showDetail(entry);
          } catch (err) {
            aiBtn.disabled = false;
            aiBtn.textContent = `Look up “${q}” with AI`;
            if (msg) msg.textContent = aiErrorMessage(err);
          }
        });
      }
      return;
    }

    // Group alphabetically by first letter for a clean glossary feel.
    const groups = new Map();
    for (const e of results) {
      const letter = (e.headword[0] || "·").toUpperCase();
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter).push(e);
    }
    const letters = [...groups.keys()].sort((a, b) => a.localeCompare(b, "es"));

    list.innerHTML = letters
      .map((letter) => {
        const items = groups.get(letter);
        return `
          <div class="mb-6">
            <h3 class="flex items-center gap-2 text-sm font-semibold text-sage-700 mb-2 sticky top-16 bg-white/80 backdrop-blur py-1 z-10">
              <span class="w-6 h-6 inline-flex items-center justify-center rounded-md bg-sage-50 text-sage-600 text-xs font-bold">${escapeHtml(letter)}</span>
              <span class="text-xs font-normal text-sage-400">${items.length}</span>
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              ${items.map((e) => cardHTML(e, q)).join("")}
            </div>
          </div>`;
      })
      .join("");

    list.querySelectorAll("[data-dict-id]").forEach((el) => {
      el.addEventListener("click", () => {
        const entry = entries.find((e) => e.id === el.dataset.dictId);
        if (entry) showDetail(entry);
      });
    });
  }

  function cardHTML(e, q) {
    const first = glossList(e)[0] || "";
    return `
      <button data-dict-id="${escapeHtml(e.id)}"
        class="bg-white border border-sage-100 hover:border-sage-300 rounded-lg p-3 text-left transition flex items-start gap-2">
        <div class="min-w-0 flex-1">
          <p class="font-semibold text-sage-800 truncate flex items-center gap-1.5">
            ${genderTag(e.gender)}${highlightMatch(e.headword, q)}
          </p>
          <p class="text-xs text-sage-500 truncate">${highlightMatch(first, q)}</p>
        </div>
        <span class="text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap border ${posBadge(e.part_of_speech)}">${escapeHtml(e.part_of_speech || "")}</span>
      </button>`;
  }

  input.addEventListener("input", render);
  app.querySelectorAll(".tier-chip").forEach((b) =>
    b.addEventListener("click", () => {
      tier = b.dataset.tier;
      paintChips();
      render();
    })
  );

  paintChips();
  render();

  // Honour a deep-link (#/dictionary?word=parlare) by opening that entry.
  if (params.word) {
    const target = entries.find((e) => e.headword.toLowerCase() === String(params.word).toLowerCase());
    if (target) showDetail(target);
  }
}

// ---- Word detail modal --------------------------------------------------

function onModalKeydown(e) {
  if (e.key === "Escape") closeDetail();
}

function closeDetail() {
  const existing = document.getElementById("dict-modal");
  if (existing) existing.remove();
  document.removeEventListener("keydown", onModalKeydown);
}

function crossLinkHTML(headword) {
  const { verbs, vocab } = buildXref();
  const key = headword.toLowerCase().trim();
  const chips = [];
  if (verbs.has(key)) {
    chips.push(
      `<button data-xverb="${escapeHtml(headword)}" class="text-xs px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 hover:border-amber-400 transition">🔤 Conjugate this verb →</button>`
    );
  }
  const card = vocab.get(key);
  if (card) {
    chips.push(
      `<button data-xvocab="${escapeHtml(card.theme || "")}" class="text-xs px-3 py-1.5 rounded-full bg-sage-50 border border-sage-200 text-sage-700 hover:border-sage-400 transition">🗂️ See in vocabulary →</button>`
    );
  }
  if (!chips.length) return "";
  return `
    <div class="mt-4">
      <p class="text-[11px] font-semibold text-sage-500 uppercase tracking-wide mb-1.5">Also in Azzurro</p>
      <div class="flex flex-wrap gap-2">${chips.join("")}</div>
    </div>`;
}

function showDetail(e) {
  closeDetail();

  const glosses = glossList(e);
  const top = glosses.slice(0, 3);
  const rest = glosses.slice(3);

  const overlay = document.createElement("div");
  overlay.id = "dict-modal";
  overlay.className =
    "fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 sm:p-6 bg-sage-900/30 backdrop-blur-sm overflow-y-auto fade-in";

  const card = document.createElement("div");
  card.className =
    "bg-white border border-sage-200 rounded-2xl p-5 sm:p-6 shadow-xl w-full max-w-lg my-8 relative";

  const sensesHTML = `
    <ol class="mt-4 space-y-1.5 list-none">
      ${top
        .map(
          (g, i) =>
            `<li class="flex gap-2 text-sage-800"><span class="text-sage-300 tabular-nums text-sm mt-0.5">${i + 1}.</span><span>${escapeHtml(g)}</span></li>`
        )
        .join("")}
      ${
        rest.length
          ? `<li id="dict-more" class="hidden">${rest
              .map(
                (g, i) =>
                  `<div class="flex gap-2 text-sage-800 mt-1.5"><span class="text-sage-300 tabular-nums text-sm mt-0.5">${top.length + i + 1}.</span><span>${escapeHtml(g)}</span></div>`
              )
              .join("")}</li>
            <li><button data-more class="text-xs font-medium text-sage-500 hover:text-sage-700 mt-1">+ ${rest.length} more sense${rest.length === 1 ? "" : "s"}</button></li>`
          : ""
      }
    </ol>`;

  card.innerHTML = `
    <button data-close aria-label="Close" class="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-sage-100 text-sage-500 flex items-center justify-center text-lg">✕</button>
    <div class="pr-10">
      <h3 class="text-2xl font-bold text-sage-800 flex items-center flex-wrap gap-2">
        ${genderTag(e.gender)}${escapeHtml(e.headword)}${speakButtonHTML(e.headword, { size: "lg" })}
      </h3>
      <div class="flex flex-wrap items-center gap-1.5 mt-2">
        <span class="text-[11px] px-2 py-0.5 rounded-full border ${posBadge(e.part_of_speech)}">${escapeHtml(e.part_of_speech || "")}</span>
        ${e.tier ? `<span class="text-[11px] px-2 py-0.5 rounded-full border bg-white text-sage-600 border-sage-200">${escapeHtml(e.tier)}</span>` : ""}
        ${e.ipa ? `<span class="text-sm text-sage-400 font-mono">${escapeHtml(e.ipa)}</span>` : ""}
      </div>
    </div>
    ${glosses.length ? sensesHTML : `<p class="mt-4 text-sage-400 italic text-sm">No definition available.</p>`}
    ${
      e.example_es
        ? `<div class="mt-4 rounded-xl border border-sage-100 bg-sand-50/60 p-4">
            <p class="text-sage-800 flex items-start gap-1.5">${escapeHtml(e.example_es)}${speakButtonHTML(e.example_es)}</p>
            ${e.example_en ? `<p class="text-sm text-sage-500 mt-1 italic">${escapeHtml(e.example_en)}</p>` : ""}
            ${e.example_source ? `<p class="text-[10px] text-sage-400 mt-1.5">${escapeHtml(e.example_source)}</p>` : ""}
          </div>`
        : ""
    }
    ${crossLinkHTML(e.headword)}
    <div data-modal-attrib class="border-t border-sage-100 mt-5 pt-1"></div>`;

  // Licence footer also lives inside the detail panel (the modal shows
  // dictionary data on its own).
  card.querySelector("[data-modal-attrib]").innerHTML = dictionaryAttributionHTML();

  overlay.appendChild(card);
  document.body.appendChild(overlay);

  const moreBtn = card.querySelector("[data-more]");
  if (moreBtn) {
    moreBtn.addEventListener("click", () => {
      card.querySelector("#dict-more")?.classList.remove("hidden");
      moreBtn.remove();
    });
  }
  card.querySelector("[data-xverb]")?.addEventListener("click", () => {
    closeDetail();
    navigate("verb-detail", { inf: card.querySelector("[data-xverb]").dataset.xverb });
  });
  card.querySelector("[data-xvocab]")?.addEventListener("click", () => {
    closeDetail();
    const theme = card.querySelector("[data-xvocab]").dataset.xvocab;
    navigate("vocabulary", theme ? { theme } : {});
  });

  overlay.addEventListener("mousedown", (ev) => {
    if (ev.target === overlay) closeDetail();
  });
  card.querySelector("[data-close]").addEventListener("click", closeDetail);
  document.addEventListener("keydown", onModalKeydown);
}
